import React from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
    Box,
    Button,
    Container,
    Divider,
    Fab,
    Link,
    Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import posts from './blogData';
import { useHasActiveAccess, SubscriberCtaContent } from '../../components/AdaptiveCta';

const cyan = '#00BFFF';
const green = '#8DC63F';

const ScrollToTopButton = () => {
    const [visible, setVisible] = React.useState(false);
    const [fade, setFade] = React.useState(false);

    React.useEffect(() => {
        const handleScroll = () => setVisible(window.scrollY > 200);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const handleClick = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setFade(true);
        setTimeout(() => { setVisible(false); setFade(false); }, 500);
    };

    if (!visible) return null;
    return (
        <Box onClick={handleClick} sx={{ position: 'fixed', bottom: 16, right: 16, zIndex: 1000, opacity: fade ? 0 : 1, transition: 'opacity 0.5s ease-out' }}>
            <Fab color="primary" aria-label="scroll back to top"><KeyboardArrowUpIcon /></Fab>
        </Box>
    );
};

export const Section = ({ title, children }) => (
    <Box sx={{ mb: 5 }}>
        <Typography variant="h4" component="h2" sx={{ fontWeight: 700, color: 'text.primary', mb: 2 }}>{title}</Typography>
        {children}
    </Box>
);

export const Para = ({ children }) => (
    <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.85, mb: 2 }}>{children}</Typography>
);

export const BulletList = ({ items, children }) => (
    <Box component="ul" sx={{ color: 'text.secondary', pl: 3, mb: 2, '& li': { mb: 1.5, lineHeight: 1.8 } }}>
        {items ? items.map((item, i) => <Box component="li" key={i}>{item}</Box>) : children}
    </Box>
);

export const NumberedList = ({ items, children }) => (
    <Box component="ol" sx={{ color: 'text.secondary', pl: 3, mb: 2, '& li': { mb: 1.5, lineHeight: 1.8 } }}>
        {items ? items.map((item, i) => <Box component="li" key={i}>{item}</Box>) : children}
    </Box>
);

export const InternalLink = ({ to, children }) => (
    <Link component={RouterLink} to={to} sx={{ color: cyan }}>{children}</Link>
);

const BOOK_PAGE_URL = '/books/read-faster-without-forgetting-everything';

// Blog posts whose topics align with the ReaderFLEX book page. On these posts,
// the "Keep Reading" section promotes the book page for internal linking.
const BOOK_PAGE_RELATED_SLUGS = new Set([
    'how-to-read-faster-and-remember-everything',
    'why-do-i-forget-what-i-read',
    'read-faster-without-losing-comprehension',
    'why-you-keep-rereading-same-paragraph',
    'how-to-remember-everything-you-read',
    '15-minute-speed-reading-routine',
]);

export const RelatedPosts = ({ currentSlug, slugs }) => {
    const promo = BOOK_PAGE_RELATED_SLUGS.has(currentSlug)
        ? [{ key: 'readerflex-book', url: BOOK_PAGE_URL, title: 'How to Read Faster Without Forgetting What You Read', readMin: 9 }]
        : [];
    const pool = slugs
        ? slugs.map((s) => posts.find((p) => p.slug === s)).filter(Boolean)
        : posts.filter((p) => p.slug !== currentSlug);
    const others = pool
        .slice(0, 3 - promo.length)
        .map((p) => ({ key: p.slug, url: `/blog/${p.slug}`, title: p.title, readMin: p.readMin }));
    const items = [...promo, ...others];
    return (
        <Box sx={{ mt: 6 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary', mb: 3 }}>Keep Reading</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {items.map((p) => (
                    <Link
                        key={p.key}
                        component={RouterLink}
                        to={p.url}
                        underline="none"
                        sx={{
                            p: 2.5,
                            borderRadius: 2,
                            border: (theme) => `1px solid ${theme.palette.divider}`,
                            transition: 'all 0.2s',
                            '&:hover': { borderColor: cyan, transform: 'translateX(4px)' },
                        }}
                    >
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary', lineHeight: 1.3 }}>{p.title}</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>{p.readMin} min read</Typography>
                            <Box sx={{
                                display: 'inline-flex', alignItems: 'center',
                                px: 1, py: 0.15, borderRadius: 1.5,
                                backgroundColor: (theme) => alpha(theme.palette.secondary.main, 0.12),
                                border: (theme) => `1px solid ${alpha(theme.palette.secondary.main, 0.3)}`,
                            }}>
                                <Typography variant="caption" sx={{ color: 'secondary.main', fontWeight: 600, fontStyle: 'italic', fontSize: '0.6rem' }}>
                                    for most people.
                                </Typography>
                            </Box>
                        </Box>
                    </Link>
                ))}
            </Box>
        </Box>
    );
};

const BlogLayout = ({ slug, title, description, date, readMin, jsonLd, children }) => {
    const navigate = useNavigate();
    const hasActiveAccess = useHasActiveAccess();
    const canonicalUrl = `https://readerflex.com/blog/${slug}`;

    return (
        <Box sx={{ overflow: 'hidden' }}>
            <Helmet>
                <title>{title} | ReaderFLEX</title>
                <meta name="description" content={description} />
                <link rel="canonical" href={canonicalUrl} />
                <meta property="og:title" content={title} />
                <meta property="og:description" content={description} />
                <meta property="og:url" content={canonicalUrl} />
                <meta property="og:type" content="article" />
                <meta property="og:site_name" content="ReaderFLEX" />
                <meta name="twitter:card" content="summary" />
                <meta name="twitter:title" content={title} />
                <meta name="twitter:description" content={description} />
                {jsonLd && (
                    <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
                )}
            </Helmet>

            {/* Hero */}
            <Box sx={{
                py: { xs: 6, md: 10 },
                position: 'relative',
                '&::before': {
                    content: '""', position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                    background: `radial-gradient(ellipse at 40% 0%, ${alpha(cyan, 0.06)} 0%, transparent 60%)`,
                    pointerEvents: 'none',
                },
            }}>
                <Container maxWidth="md">
                    <Typography variant="overline" sx={{ color: cyan, letterSpacing: 2, mb: 1, display: 'block' }}>
                        <Link component={RouterLink} to="/blog" sx={{ color: cyan, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>ReaderFlex Blog</Link>
                    </Typography>
                    <Typography variant="h2" component="h1" sx={{ fontWeight: 800, color: 'text.primary', mb: 2, fontSize: { xs: '2rem', md: '2.75rem' }, lineHeight: 1.2 }}>
                        {title}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            Published {date} &middot; {readMin} min read
                        </Typography>
                        <Box sx={{
                            display: 'inline-flex', alignItems: 'center', gap: 0.5,
                            px: 1.5, py: 0.4, borderRadius: 2,
                            backgroundColor: (theme) => alpha(theme.palette.secondary.main, 0.12),
                            border: (theme) => `1px solid ${alpha(theme.palette.secondary.main, 0.3)}`,
                        }}>
                            <Typography variant="caption" sx={{ color: 'secondary.main', fontWeight: 600, fontStyle: 'italic', letterSpacing: 0.3 }}>
                                for most people.
                            </Typography>
                        </Box>
                    </Box>
                </Container>
            </Box>

            <Container maxWidth="md" sx={{ pb: { xs: 8, md: 12 } }}>
                <article>
                    {children}
                </article>

                <Divider sx={{ my: 5, borderColor: 'rgba(0, 191, 255, 0.12)' }} />

                <RelatedPosts currentSlug={slug} />

                {/* Free Speed Test CTA */}
                {!hasActiveAccess && (
                    <Box sx={{
                        mt: 6, p: 4, borderRadius: 3,
                        border: `1px solid ${alpha(cyan, 0.25)}`,
                        background: alpha(cyan, 0.04),
                        display: 'flex', flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: 'center', gap: 3,
                    }}>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
                                Find Out How Fast You Read
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                Free reading speed and comprehension test. No account required.
                            </Typography>
                        </Box>
                        <Button
                            variant="contained"
                            size="large"
                            onClick={() => navigate('/speed-test')}
                            sx={{ py: 1.5, px: 4, whiteSpace: 'nowrap', flexShrink: 0 }}
                        >
                            Take the Free Test
                        </Button>
                    </Box>
                )}

                {/* Final CTA */}
                <Box sx={{
                    mt: 4, p: 5, borderRadius: 3, textAlign: 'center',
                    border: `1px solid ${alpha(cyan, 0.2)}`,
                    background: `linear-gradient(135deg, ${alpha(cyan, 0.05)} 0%, ${alpha(green, 0.05)} 100%)`,
                }}>
                    {hasActiveAccess ? (
                        <SubscriberCtaContent />
                    ) : (
                        <>
                            <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', mb: 1 }}>
                                Try ReaderFlex for $1
                            </Typography>
                            <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3, maxWidth: 500, mx: 'auto' }}>
                                Full access for 3 days. Use the Guide Dot on real articles. Take quizzes. See your stats. No auto-billing.
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                                <Button variant="contained" color="secondary" size="large" onClick={() => navigate('/register')} sx={{ py: 1.5, px: 5 }}>
                                    Start Your Trial
                                </Button>
                                <Button variant="outlined" color="primary" size="large" onClick={() => navigate('/subscription')} sx={{ py: 1.5, px: 5 }}>
                                    View Pricing
                                </Button>
                            </Box>
                        </>
                    )}
                </Box>
            </Container>

            <ScrollToTopButton />
        </Box>
    );
};

export default BlogLayout;
